import type { ElementType, MouseEvent } from 'react';

import { S } from './Button.styles';
import type { ButtonProps } from './Button.types';

export const Button = <C extends ElementType = 'button'>({
  children,
  component,
  corner = 'rounded',
  disabled,
  fullWidth,
  leftIcon,
  rightIcon,
  size = 'medium',
  textAlign,
  type = 'button',
  variant = 'filled',
  color = variant === 'text' ? 'secondary' : 'primary',
  ref,
  href,
  onClick: onClickFromProps,
  ...otherProps
}: ButtonProps<C>) => {
  const Element = component || (href ? 'a' : 'button');

  const isButton = Element === 'button';
  const onClick = (e: MouseEvent<HTMLElement>) => {
    if (disabled) {
      e.preventDefault();

      return;
    }
    onClickFromProps?.(e);
  };

  return (
    <S.Button
      as={Element}
      {...(isButton
        ? { disabled, type }
        : {
            href,
            ...(disabled && { 'aria-disabled': true, tabIndex: -1 }),
            ...(Element !== 'a' && { role: 'button' as const }),
          })}
      ref={ref}
      $color={color}
      $corner={corner}
      $fullWidth={fullWidth}
      $size={size}
      $textAlign={textAlign}
      $variant={variant}
      onClick={onClick}
      {...otherProps}
    >
      {leftIcon && <S.Icon>{leftIcon}</S.Icon>}
      {children}
      {rightIcon && <S.Icon>{rightIcon}</S.Icon>}
    </S.Button>
  );
};
